import {
	arrow,
	autoUpdate,
	computePosition,
	flip,
	type Middleware,
	offset,
	type Placement,
	shift,
} from '@floating-ui/dom'
import { Check, ChevronLeft, ChevronRight, Maximize2, X } from 'lucide-react'
import { useCallback, useEffect, useId, useLayoutEffect, useRef } from 'react'
import type { useSummaryTriage } from '@/components/analysis/summary/process-summary/tabs/hooks/useSummaryTriage'
import type { SummaryItem } from '@/components/analysis/summary/types/triage'
import { styleForType } from '@/components/bpmn/improvements/improvementTypeStyles'
import type { RelatedStepRef } from '@/components/bpmn/improvements/mapImprovementsToElements'
import { useTranslation } from '@/hooks/useTranslation'
import { cn } from '@/lib/utils'

const FOCUSABLE =
	'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

const CANDIDATE_PLACEMENTS: Placement[] = ['right-start', 'left-start', 'bottom', 'top']
const DEFAULT_PLACEMENT: Placement = 'right-start'
const HIGHLIGHT_SELECTOR = '.djs-element.mm-spot-selected, .djs-element.mm-spot-related'
const SELECTED_SELECTOR = '.djs-element.mm-spot-selected'
const RELATED_SELECTOR = '.djs-element.mm-spot-related'
/** How long to wait for the spotlight markers before placing the card without them. */
const SPOTLIGHT_SETTLE_TIMEOUT_MS = 400
const BOUNDS_TOLERANCE_PX = 0.5

export type ReviewCardPositionAnchor =
	| HTMLElement
	| {
			getBoundingClientRect: () => DOMRect
			contextElement?: Element
	  }

/** The steps `useSpotlight` is expected to mark for the open item. */
export interface ReviewCardSpotlight {
	primaryBpmnId: string
	relatedBpmnIds: readonly string[]
}

interface ImprovementReviewCardProps {
	item: SummaryItem
	idx: number
	total: number
	elementIndex: number
	elementTotal: number
	anchor: ReviewCardPositionAnchor
	clickAnchor: HTMLElement
	boundary: HTMLElement
	spotlight?: ReviewCardSpotlight | null
	triage: ReturnType<typeof useSummaryTriage>
	readOnly?: boolean
	onClose: () => void
	onExpand: () => void
	onAddressed: () => void
	onElementPrev: () => void
	onElementNext: () => void
	relatedSteps?: RelatedStepRef[]
	hoveredBpmnId?: string | null
	onHoverRelated?: (bpmnId: string | null) => void
	onFocusRelated?: (bpmnId: string) => void
}

function anchorContext(anchor: ReviewCardPositionAnchor): Element | null {
	if (anchor instanceof HTMLElement) return anchor
	return anchor.contextElement ?? null
}

function isAnchorMounted(anchor: ReviewCardPositionAnchor): boolean {
	const el = anchorContext(anchor)
	return Boolean(el && document.contains(el))
}

interface Box {
	left: number
	top: number
	width: number
	height: number
}

function overlapArea(box: Box, rects: DOMRect[]): number {
	let area = 0
	for (const rect of rects) {
		const left = Math.max(box.left, rect.left)
		const top = Math.max(box.top, rect.top)
		const right = Math.min(box.left + box.width, rect.right)
		const bottom = Math.min(box.top + box.height, rect.bottom)
		const width = right - left
		const height = bottom - top
		if (width > 0 && height > 0) area += width * height
	}
	return area
}

function fitsInside(box: Box, bounds: DOMRect): boolean {
	return (
		box.left >= bounds.left - BOUNDS_TOLERANCE_PX &&
		box.top >= bounds.top - BOUNDS_TOLERANCE_PX &&
		box.left + box.width <= bounds.right + BOUNDS_TOLERANCE_PX &&
		box.top + box.height <= bounds.bottom + BOUNDS_TOLERANCE_PX
	)
}

function rectKey(rect: DOMRect): string {
	return `${Math.round(rect.left)},${Math.round(rect.top)},${Math.round(rect.width)},${Math.round(rect.height)}`
}

interface Scene {
	/** Changes when the canvas, the anchor, or any spotlighted step moves on screen. */
	key: string
	/** Screen rects of the spotlighted steps, selected step included. */
	highlights: DOMRect[]
}

function measureScene(boundary: HTMLElement, anchor: ReviewCardPositionAnchor): Scene {
	const highlights = [...boundary.querySelectorAll(HIGHLIGHT_SELECTOR)].map((el) =>
		el.getBoundingClientRect()
	)
	const key = [boundary.getBoundingClientRect(), anchor.getBoundingClientRect(), ...highlights]
		.map(rectKey)
		.join('|')
	return { key, highlights }
}

function elementIdOf(el: Element): string | null {
	return el.getAttribute('data-element-id')
}

/**
 * True once the markers in the DOM belong to `expected`. On Next/Prev the
 * previous item's markers are still painted during the first frame, so a
 * `.mm-spot-selected` alone is not proof that the new item's spotlight is up.
 */
function spotlightPainted(boundary: HTMLElement, expected: ReviewCardSpotlight): boolean {
	const selected = boundary.querySelector(SELECTED_SELECTOR)
	if (!selected || elementIdOf(selected) !== expected.primaryBpmnId) return false
	const painted = new Set(
		[...boundary.querySelectorAll(RELATED_SELECTOR)].map((el) => elementIdOf(el))
	)
	const wanted = new Set(expected.relatedBpmnIds.filter((id) => id !== expected.primaryBpmnId))
	if (painted.size !== wanted.size) return false
	for (const id of wanted) {
		if (!painted.has(id)) return false
	}
	return true
}

function spotlightTokens(value: string | null): string {
	if (!value) return ''
	return value
		.split(/\s+/)
		.filter((token) => token.startsWith('mm-spot') && !token.includes('hovered'))
		.sort()
		.join(' ')
}

/** Ignore hover restyles (`mm-spot-hovered`, tag classes); only marker changes matter. */
function spotlightMarkersChanged(record: MutationRecord): boolean {
	const target = record.target
	if (!(target instanceof Element)) return false
	return spotlightTokens(record.oldValue) !== spotlightTokens(target.getAttribute('class'))
}

type PositionResult = Awaited<ReturnType<typeof computePosition>>

function positionAt(
	anchor: ReviewCardPositionAnchor,
	panel: HTMLElement,
	placement: Placement,
	middleware: Middleware[]
): Promise<PositionResult> {
	return computePosition(anchor, panel, { strategy: 'fixed', placement, middleware })
}

/**
 * Pick the candidate side that keeps the card inside the canvas and off the
 * spotlighted steps. `preferred` (the side the card is already on) wins ties
 * and is kept outright when it is clear, so panning does not flip the card.
 */
async function choosePlacement(
	anchor: ReviewCardPositionAnchor,
	panel: HTMLElement,
	boundary: HTMLElement,
	middleware: Middleware[],
	highlights: DOMRect[],
	preferred: Placement | null
): Promise<PositionResult> {
	const bounds = boundary.getBoundingClientRect()
	const order = preferred
		? [preferred, ...CANDIDATE_PLACEMENTS.filter((placement) => placement !== preferred)]
		: CANDIDATE_PLACEMENTS
	let best: (PositionResult & { overlap: number }) | null = null

	for (const placement of order) {
		const result = await positionAt(anchor, panel, placement, middleware)
		const box = {
			left: result.x,
			top: result.y,
			width: panel.offsetWidth,
			height: panel.offsetHeight,
		}
		if (!fitsInside(box, bounds)) continue
		const overlap = overlapArea(box, highlights)
		if (!best || overlap < best.overlap) {
			best = { ...result, overlap }
		}
		if (best.overlap === 0) break
	}

	if (best) return best

	return computePosition(anchor, panel, {
		strategy: 'fixed',
		placement: DEFAULT_PLACEMENT,
		middleware: [
			offset(12),
			flip({ boundary, padding: 8, fallbackPlacements: ['left-start', 'bottom', 'top'] }),
			shift({ boundary, padding: 8, crossAxis: true }),
			...middleware.filter((entry) => entry.name === 'arrow'),
		],
	})
}

export function ImprovementReviewCard({
	item,
	idx,
	total,
	elementIndex,
	elementTotal,
	anchor,
	clickAnchor,
	boundary,
	spotlight = null,
	triage,
	readOnly,
	onClose,
	onExpand,
	onAddressed,
	onElementPrev,
	onElementNext,
	relatedSteps = [],
	hoveredBpmnId = null,
	onHoverRelated,
	onFocusRelated,
}: ImprovementReviewCardProps) {
	const { t } = useTranslation()
	const titleId = useId()
	const hintId = useId()
	const panelRef = useRef<HTMLDivElement>(null)
	const arrowRef = useRef<HTMLDivElement>(null)
	const noteRef = useRef<HTMLTextAreaElement>(null)

	const status = triage.getStatus(item.id, item)
	const kind = item.type ?? 'question'
	const style = styleForType(kind)
	const KindIcon = style.Icon
	const basis = item.basis ?? 'inferred'
	const inferred = basis !== 'observed'
	const draft = triage.getResolution(item.id)
	const hasSolution = Boolean(draft.trim())
	const isNeeds = status === 'needs_verification'
	const isConfirmed = status === 'confirmed'
	const showSolution = isConfirmed && !readOnly
	const showConfirmActions = isNeeds && !readOnly
	const showAddressActions = isConfirmed && !readOnly
	const persistRef = useRef({ id: item.id, setDraft: triage.setDraft })
	persistRef.current = { id: item.id, setDraft: triage.setDraft }
	// Read inside the placement effect without restarting it: the spotlight
	// prop is already set on the render that mounts the card, so it says what
	// to wait for, not when the markers are painted.
	const spotlightRef = useRef(spotlight)
	spotlightRef.current = spotlight

	const flushDraft = useCallback(() => {
		if (noteRef.current) triage.setDraft(item.id, noteRef.current.value)
	}, [item.id, triage.setDraft])

	// biome-ignore lint/correctness/useExhaustiveDependencies: retrigger placement when the open item changes
	useLayoutEffect(() => {
		const panel = panelRef.current
		const arrowEl = arrowRef.current
		if (!panel) return
		let cancelled = false
		// Side the card sits on. Null until the first decision against the
		// painted spotlight (or against none, when this open has no spotlight).
		let locked: Placement | null = null
		// Scene the current side was decided on; a different scene re-opens
		// the decision, while typing or hover restyles leave it alone.
		let decidedScene: string | null = null
		let spotlightTimedOut = false
		let settleTimer: number | null = null
		let sequence = 0
		// Where the card was for the previous item, so Next/Prev only moves it
		// when the new highlights make that side a bad one.
		const previous = (panel.dataset.placement as Placement | undefined) ?? null

		const followMiddleware: Middleware[] = [
			offset(12),
			shift({ boundary, padding: 8, crossAxis: true }),
		]
		if (arrowEl) {
			followMiddleware.push(arrow({ element: arrowEl, padding: 10 }))
		}

		const applyPlacement = ({ x, y, placement, middlewareData }: PositionResult) => {
			if (cancelled || !panel.isConnected) return
			panel.style.left = `${x}px`
			panel.style.top = `${y}px`
			panel.dataset.placement = placement
			if (locked) {
				delete panel.dataset.settling
			} else {
				panel.dataset.settling = ''
			}
			if (arrowEl && middlewareData.arrow) {
				const { x: arrowX, y: arrowY } = middlewareData.arrow
				arrowEl.style.left = arrowX != null ? `${arrowX}px` : ''
				arrowEl.style.top = arrowY != null ? `${arrowY}px` : ''
			}
		}

		const armSettleTimer = () => {
			if (settleTimer != null) return
			settleTimer = window.setTimeout(() => {
				settleTimer = null
				spotlightTimedOut = true
				update()
			}, SPOTLIGHT_SETTLE_TIMEOUT_MS)
		}

		const update = () => {
			if (cancelled || !isAnchorMounted(anchor) || !panel.isConnected) return
			const seq = ++sequence
			const scene = measureScene(boundary, anchor)
			const expected = spotlightRef.current
			const spotlightReady = !expected || spotlightTimedOut || spotlightPainted(boundary, expected)

			let job: Promise<PositionResult>
			if (locked && scene.key === decidedScene) {
				job = positionAt(anchor, panel, locked, followMiddleware)
			} else if (!locked && !spotlightReady) {
				// The markers (and the fit that follows them) are not in yet:
				// hold a provisional spot, hidden, and decide once they land.
				armSettleTimer()
				job = positionAt(anchor, panel, previous ?? DEFAULT_PLACEMENT, followMiddleware)
			} else {
				job = choosePlacement(
					anchor,
					panel,
					boundary,
					followMiddleware,
					scene.highlights,
					locked ?? previous
				).then((result) => {
					if (seq === sequence) {
						locked = result.placement
						decidedScene = scene.key
					}
					return result
				})
			}
			void job
				.then((result) => {
					if (seq === sequence) applyPlacement(result)
				})
				.catch(() => {
					// Anchor may have unmounted while computePosition was in flight.
				})
		}

		const stop = autoUpdate(anchor, panel, update, { elementResize: false })
		// useSpotlight paints its markers and fits the view after this effect
		// has run, so watch the canvas for them instead of guessing the timing.
		const observer = new MutationObserver((records) => {
			if (records.some(spotlightMarkersChanged)) update()
		})
		observer.observe(boundary, {
			subtree: true,
			attributes: true,
			attributeFilter: ['class'],
			attributeOldValue: true,
		})
		// Pan and zoom set the viewport transform. autoUpdate only hears DOM
		// scroll and resize, so follow the step from the attribute itself.
		const viewport = boundary.querySelector('.viewport')
		const viewportObserver = new MutationObserver(() => update())
		if (viewport) {
			viewportObserver.observe(viewport, { attributes: true, attributeFilter: ['transform'] })
		}
		return () => {
			cancelled = true
			observer.disconnect()
			viewportObserver.disconnect()
			if (settleTimer != null) window.clearTimeout(settleTimer)
			stop()
		}
	}, [anchor, boundary, item.id])

	useEffect(() => {
		if (showSolution) {
			noteRef.current?.focus()
		} else {
			panelRef.current?.focus()
		}
	}, [showSolution])

	useEffect(() => {
		const onKey = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				event.preventDefault()
				event.stopPropagation()
				flushDraft()
				onClose()
			}
		}
		window.addEventListener('keydown', onKey, true)
		return () => window.removeEventListener('keydown', onKey, true)
	}, [onClose, flushDraft])

	useEffect(() => {
		const onPointer = (event: MouseEvent) => {
			const target = event.target as Node | null
			if (!target) return
			if (panelRef.current?.contains(target) || clickAnchor.contains(target)) return
			if (boundary.contains(target)) return
			flushDraft()
			onClose()
		}
		document.addEventListener('mousedown', onPointer)
		return () => document.removeEventListener('mousedown', onPointer)
	}, [boundary, clickAnchor, flushDraft, onClose])

	useEffect(() => {
		const panel = panelRef.current
		if (!panel) return
		const onKey = (event: KeyboardEvent) => {
			if (event.key !== 'Tab') return
			const nodes = [...panel.querySelectorAll<HTMLElement>(FOCUSABLE)].filter(
				(el) => el.offsetParent !== null || el === document.activeElement
			)
			if (nodes.length === 0) return
			const first = nodes[0]
			const last = nodes[nodes.length - 1]
			if (event.shiftKey && document.activeElement === first) {
				event.preventDefault()
				last?.focus()
			} else if (!event.shiftKey && document.activeElement === last) {
				event.preventDefault()
				first?.focus()
			}
		}
		panel.addEventListener('keydown', onKey)
		return () => panel.removeEventListener('keydown', onKey)
	}, [])

	useEffect(() => {
		return () => {
			const { id, setDraft } = persistRef.current
			if (noteRef.current) setDraft(id, noteRef.current.value)
		}
	}, [])

	const handleAddress = async () => {
		flushDraft()
		const ok = await triage.transition(item, 'address')
		if (ok) onAddressed()
	}

	const handleSendBack = async () => {
		flushDraft()
		await triage.transition(item, 'send_back')
	}

	const handleConfirm = async () => {
		await triage.transition(item, 'confirm')
	}

	const handleDismiss = async () => {
		if (readOnly) return
		const ok = await triage.transition(item, 'dismiss')
		if (ok) onAddressed()
	}

	return (
		<div
			ref={panelRef}
			role="dialog"
			aria-labelledby={titleId}
			tabIndex={-1}
			className={cn(
				'bpmn-improvement-card fixed z-[120] w-[360px] rounded-[14px] bg-white shadow-[0_8px_28px_rgb(0_0_0/0.16)] outline-none',
				`bpmn-improvement-card--${kind}`
			)}
		>
			<div className="bpmn-improvement-card__bar" />
			<div ref={arrowRef} className="bpmn-improvement-card-arrow" aria-hidden />

			<div className="flex items-center gap-1.5 px-3.5 pt-2.5">
				<span className="bpmn-improvement-card__type inline-flex items-center gap-1 text-[11.5px] font-medium">
					<KindIcon className="size-3" aria-hidden />
					{t(style.labelKey)}
				</span>
				<span className="text-[11.5px] text-[#9A9A93]">
					{inferred
						? t('pages.analysisDetail.structuredSummaryEditor.reviewBoard.basisInferred')
						: t('pages.analysisDetail.structuredSummaryEditor.reviewBoard.basisObserved')}
					{' · '}
					{t('pages.analysisDetail.structuredSummaryEditor.reviewBoard.positionOf', {
						n: idx + 1,
						m: total,
					})}
				</span>
				<div className="ml-auto flex shrink-0 items-center gap-0.5">
					<button
						type="button"
						onClick={() => {
							flushDraft()
							onExpand()
						}}
						className="rounded-md p-1 text-[#9A9A93] hover:bg-[#FAFAF8]"
						aria-label={t('components.bpmnEditor.improvements.expand')}
					>
						<Maximize2 className="size-3.5" aria-hidden />
					</button>
					<button
						type="button"
						onClick={() => {
							flushDraft()
							onClose()
						}}
						className="rounded-md p-1 text-[#9A9A93] hover:bg-[#FAFAF8]"
						aria-label={t('pages.analysisDetail.structuredSummaryEditor.reviewBoard.closeModal')}
					>
						<X className="size-3.5" aria-hidden />
					</button>
				</div>
			</div>

			{elementTotal > 1 ? (
				<div className="mt-1 flex items-center justify-end gap-1 px-3.5">
					<button
						type="button"
						onClick={() => {
							flushDraft()
							onElementPrev()
						}}
						disabled={elementIndex <= 0}
						className="rounded-md p-0.5 text-[#9A9A93] hover:bg-[#FAFAF8] disabled:opacity-30"
						aria-label={t('pages.analysisDetail.structuredSummaryEditor.reviewBoard.prevItem')}
					>
						<ChevronLeft className="size-3.5" aria-hidden />
					</button>
					<span className="text-[11px] tabular-nums text-[#9A9A93]">
						{t('pages.analysisDetail.structuredSummaryEditor.reviewBoard.positionOf', {
							n: elementIndex + 1,
							m: elementTotal,
						})}
					</span>
					<button
						type="button"
						onClick={() => {
							flushDraft()
							onElementNext()
						}}
						disabled={elementIndex >= elementTotal - 1}
						className="rounded-md p-0.5 text-[#9A9A93] hover:bg-[#FAFAF8] disabled:opacity-30"
						aria-label={t('pages.analysisDetail.structuredSummaryEditor.reviewBoard.nextItem')}
					>
						<ChevronRight className="size-3.5" aria-hidden />
					</button>
				</div>
			) : null}

			<div className="px-3.5 pt-2 pb-3">
				<h2 id={titleId} className="text-[15px] font-semibold leading-[1.3] text-[#1F1F1D]">
					{item.title}
				</h2>

				{item.sourceQuote ? (
					<button
						type="button"
						onClick={() => {
							flushDraft()
							onExpand()
						}}
						className="mt-2 w-full rounded-none border-l-[3px] border-[#D6D3CE] py-0 pr-0 pl-[11px] text-left font-[family-name:var(--font-voice)] text-[14px] leading-[1.5] text-[#1F1F1D]"
					>
						<span className="line-clamp-2">{item.sourceQuote}</span>
					</button>
				) : null}

				{relatedSteps.length > 0 ? (
					<div className="mt-3">
						<p className="text-[11.5px] text-[#6B6B65]">
							{t('components.bpmnEditor.improvements.alsoAffected')}
						</p>
						<ul className="mt-1 flex flex-wrap gap-1">
							{relatedSteps.map((step) => {
								const n = step.stepNumber ?? step.summaryId
								const hovered = Boolean(step.bpmnId && hoveredBpmnId === step.bpmnId)
								return (
									<li key={step.summaryId}>
										<button
											type="button"
											disabled={step.missing || !step.bpmnId}
											title={
												step.missing ? t('components.bpmnEditor.improvements.relatedStepMissing') : undefined
											}
											onMouseEnter={() => {
												if (step.bpmnId) onHoverRelated?.(step.bpmnId)
											}}
											onMouseLeave={() => onHoverRelated?.(null)}
											onFocus={() => {
												if (step.bpmnId) onHoverRelated?.(step.bpmnId)
											}}
											onBlur={() => onHoverRelated?.(null)}
											onClick={() => {
												if (step.bpmnId) onFocusRelated?.(step.bpmnId)
											}}
											className={cn(
												'rounded-md border px-2 py-0.5 text-[11.5px]',
												step.missing
													? 'cursor-not-allowed border-[#E6E4E0] bg-[#FAFAF8] text-[#A9A79F]'
													: hovered
														? cn(
																'text-white',
																kind === 'bottleneck' && 'border-[#BE123C] bg-[#BE123C]',
																kind === 'exception' && 'border-[#B45309] bg-[#B45309]',
																kind === 'question' && 'border-[#1D4ED8] bg-[#1D4ED8]'
															)
														: 'border-[#E6E4E0] bg-white text-[#4A4A46] hover:border-[#C9B6F0] hover:bg-[#F8F4FE]'
											)}
											aria-label={t('components.bpmnEditor.improvements.relatedStepAria', {
												n,
												title: step.title ?? n,
											})}
										>
											{t('components.bpmnEditor.improvements.relatedStep', { n })}
										</button>
									</li>
								)
							})}
						</ul>
					</div>
				) : null}

				{showSolution ? (
					<div className="mt-3">
						<label htmlFor={`${titleId}-solution`} className="block text-[11.5px] text-[#6B6B65]">
							{t('pages.analysisDetail.structuredSummaryEditor.reviewBoard.addNote')}
						</label>
						<textarea
							id={`${titleId}-solution`}
							ref={noteRef}
							value={draft}
							onChange={(e) => triage.setDraft(item.id, e.target.value)}
							onBlur={flushDraft}
							readOnly={readOnly}
							className="mt-1 min-h-[54px] w-full resize-none rounded-lg border border-[#E6E4E0] px-2.5 py-2 text-[13px] text-[#1F1F1D] outline-none focus:border-[#7C3AED]"
							placeholder={t(
								'pages.analysisDetail.structuredSummaryEditor.reviewBoard.solutionPlaceholder'
							)}
						/>
					</div>
				) : null}

				{showConfirmActions ? (
					<div className="mt-3 flex flex-wrap items-center gap-2">
						<button
							type="button"
							onClick={() => {
								void handleConfirm()
							}}
							className="inline-flex items-center gap-1.5 rounded-lg bg-[#8c52ff] px-3 py-2 text-[12.5px] font-medium text-white"
						>
							<Check className="size-3.5" aria-hidden />
							{t('pages.analysisDetail.structuredSummaryEditor.reviewBoard.relevant')}
						</button>
						<button
							type="button"
							onClick={() => {
								void handleDismiss()
							}}
							className="inline-flex items-center gap-1.5 rounded-lg border border-[#C9B6F0] bg-[#F8F4FE] px-3 py-2 text-[12.5px] font-medium text-[#6D28D9]"
						>
							<X className="size-3.5" aria-hidden />
							{t('pages.analysisDetail.structuredSummaryEditor.reviewBoard.irrelevant')}
						</button>
						<span className="text-[11px] text-[#9A9A93]">
							{t('pages.analysisDetail.structuredSummaryEditor.reviewBoard.hintNothingChanges')}
						</span>
					</div>
				) : null}

				{showAddressActions ? (
					<div className="mt-3 flex flex-wrap items-center gap-2">
						<button
							type="button"
							disabled={!hasSolution}
							aria-describedby={!hasSolution ? hintId : undefined}
							onClick={() => {
								void handleAddress()
							}}
							className={cn(
								'rounded-lg px-3 py-2 text-[12.5px] font-medium',
								hasSolution ? 'bg-[#8c52ff] text-white' : 'bg-[#EFEDE9] text-[#A9A79F]'
							)}
						>
							{t('pages.analysisDetail.structuredSummaryEditor.reviewBoard.applyChangeModel')}
						</button>
						<button
							type="button"
							onClick={() => {
								void handleSendBack()
							}}
							className="rounded-lg border border-[#E6E4E0] px-3 py-2 text-[12.5px] font-medium text-[#6B6B65]"
						>
							{t('pages.analysisDetail.structuredSummaryEditor.reviewBoard.sendBack')}
						</button>
						{!hasSolution ? (
							<span id={hintId} className="text-[11px] text-[#9A9A93]">
								{t('pages.analysisDetail.structuredSummaryEditor.reviewBoard.hintWriteSolution')}
							</span>
						) : null}
					</div>
				) : null}
			</div>
		</div>
	)
}
